/**
 * features/auth/hooks/useActiveStoreConfigSync.ts
 *
 * Mantiene cajero.storeConfig SINCRONIZADO con la tienda activa.
 *
 * Problema que resuelve: setActiveContext cambia activeStoreId/activeBusinessId
 * pero NO recargaba storeConfig. Al cambiar de tienda, el POS/Caja seguían
 * usando la config de la tienda de login (stale) → acciones deshabilitadas por
 * error (allow_cash_in/out, allow_credit, etc.).
 *
 * Este hook recalcula storeConfig cuando cambia la tienda/empresa activa, con
 * la MISMA logica que el login (config de tienda + herencia de empresa). Solo
 * lee (Dexie→Supabase) y actualiza el estado local del cajero. No toca DB/RLS,
 * ni ventas, ni caja, ni reglas de negocio.
 */

import { useEffect } from 'react'
import { db } from '@lib/dexie/schema'
import { supabase } from '@lib/supabase/client'
import { useCajeroStore } from './useAuth'
import { getStoreConfig } from '@features/organization/utils/storeConfig'
import {
  getCompanyConfig,
  resolveEffectiveStoreConfig,
} from '@features/organization/utils/companyConfig'

async function loadStoreConfigRaw(storeId: string): Promise<Record<string, unknown>> {
  const local = await db.stores.get(storeId).catch(() => null)
  if (local?.config) return local.config as Record<string, unknown>
  const { data } = await supabase.from('stores').select('config').eq('id', storeId).maybeSingle()
  return ((data?.config as Record<string, unknown>) ?? {}) as Record<string, unknown>
}

async function loadBusinessConfigRaw(businessId: string): Promise<Record<string, unknown>> {
  const local = await db.businesses.get(businessId).catch(() => null)
  if (local?.config) return local.config as Record<string, unknown>
  const { data } = await supabase
    .from('businesses')
    .select('config')
    .eq('id', businessId)
    .maybeSingle()
  return ((data?.config as Record<string, unknown>) ?? {}) as Record<string, unknown>
}

export function useActiveStoreConfigSync() {
  const activeStoreId = useCajeroStore(s => s.cajero?.activeStoreId ?? null)
  const activeBusinessId = useCajeroStore(s => s.cajero?.activeBusinessId ?? null)

  useEffect(() => {
    if (!activeStoreId) return
    let cancelled = false

    void (async () => {
      const rawStore = await loadStoreConfigRaw(activeStoreId)
      const rawBiz = activeBusinessId ? await loadBusinessConfigRaw(activeBusinessId) : {}
      if (cancelled) return

      // Misma derivación que el login: defaults de tienda + herencia de empresa.
      const companyConf = getCompanyConfig(rawBiz)
      const effective = resolveEffectiveStoreConfig(
        companyConf,
        getStoreConfig(rawStore)
      ) as unknown as Record<string, unknown>

      const current = useCajeroStore.getState().cajero
      // El contexto pudo cambiar durante el fetch: no pisar config de otra tienda.
      if (!current || current.activeStoreId !== activeStoreId) return
      // Evitar re-render innecesario si no cambió.
      if (JSON.stringify(current.storeConfig) === JSON.stringify(effective)) return

      useCajeroStore.getState().setCajero({ ...current, storeConfig: effective })
    })()

    return () => {
      cancelled = true
    }
  }, [activeStoreId, activeBusinessId])
}
